import React, { useState } from "react";
import { Phone, Mail, Loader2, CheckCircle2, Send, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useReveal, CONTACTO } from "./landingUtils";
import { Logo } from "./Hero";

const INTERESES = ["Un informe puntual", "Suscripción mensual", "Suscripción anual", "Aún no lo sé"];
const EMPTY = { nombre: "", farmacia: "", telefono: "", email: "", poblacion: "", interes: "", comentarios: "", website: "" };

function Field({ label, children }) {
  return <label className="block text-sm font-semibold text-slate-700">{label}<div className="mt-1.5">{children}</div></label>;
}
const inp = "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1f6fd1]/40 focus:border-[#1f6fd1] focus:bg-white transition-colors";

export function BloqueFarmaClass() {
  const [ref, cls] = useReveal();
  return (
    <section className="py-20 sm:py-24 bg-white" data-testid="landing-farmaclass">
      <div ref={ref} className={"max-w-7xl mx-auto px-5 sm:px-8 " + cls}>
        <div className="relative rounded-3xl overflow-hidden bg-[#06281f] text-white grid lg:grid-cols-[1.3fr_1fr]">
          <img src="https://farmaclass.es/farmaclass-hero.jpg" alt="FarmaClass · analítica de genéricos" className="absolute inset-0 w-full h-full object-cover opacity-30" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#06281f] via-[#06281f]/90 to-[#06281f]/40" />
          <div className="relative p-8 sm:p-12 lg:p-14">
            <div className="font-mono text-xs font-semibold uppercase tracking-widest text-emerald-300">FarmaClass · el complemento de Farmanaliza</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-3 leading-tight">¿Quieres aumentar tu <span className="text-emerald-400">rentabilidad</span>?</h2>
            <p className="text-emerald-50/85 mt-5 text-base md:text-lg leading-relaxed max-w-xl">
              Farmanaliza libera tu stock parado. FarmaClass analiza tu dispensación, separa marca de genérico molécula a molécula y te dice dónde pierdes dinero y cómo recuperarlo.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="https://farmaclass.es/informacion" target="_blank" rel="noopener noreferrer" data-testid="landing-farmaclass-cta"
                 className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition-colors">
                Descubre FarmaClass <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a href="https://farmaclass.es/dossier-rentabilidad-farmaclass.pdf" target="_blank" rel="noopener noreferrer" data-testid="landing-farmaclass-dossier"
                 className="inline-flex items-center px-6 py-3.5 rounded-full border border-white/30 font-semibold hover:bg-white/10 transition-colors">Ver el dossier</a>
            </div>
          </div>
          <div className="relative grid grid-cols-2 gap-px bg-white/10 self-stretch">
            {[["13.504 €", "beneficio medio al año"], ["+9,01 pts", "más cuota de genérico"], ["48.122 €", "la mejor farmacia, al año"], ["69", "farmacias ya lo usan"]].map(([v, l]) => (
              <div key={l} className="bg-[#06281f]/80 backdrop-blur p-6 sm:p-8 flex flex-col justify-center">
                <div className="font-display text-2xl sm:text-3xl font-extrabold text-emerald-400">{v}</div>
                <div className="text-xs text-emerald-50/75 mt-1">{l}</div>
              </div>
            ))}
            <p className="col-span-2 bg-[#06281f]/80 px-6 py-3 text-[10px] text-emerald-50/50">Datos publicados por FarmaClass.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Contacto() {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [ref, cls] = useReveal();
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.telefono.trim() && !f.email.trim()) return toast.error("Déjanos un teléfono o un email para contactarte");
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v.trim() || null]));
      await api.post("/public/leads", body);
      setDone(true);
      setF(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo enviar. Inténtalo de nuevo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contacto" className="py-24 sm:py-32 bg-white" data-testid="landing-contacto">
      <div ref={ref} className={"max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.15fr] gap-12 lg:gap-20 " + cls}>
        <div>
          <div className="font-mono text-xs font-semibold uppercase tracking-widest text-[#1f6fd1]">Sin compromiso</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#12305e] mt-3">Solicita información</h2>
          <p className="text-slate-600 mt-5 text-base md:text-lg leading-relaxed">Déjanos tus datos y te contamos cómo funciona con el stock de tu farmacia. Te contactamos en menos de 24 horas, sin compromiso y sin instalar nada.</p>
          <div className="mt-10 space-y-4">
            <a href={CONTACTO.telHref} className="flex items-center gap-4 group" data-testid="contacto-tel">
              <span className="h-12 w-12 rounded-2xl bg-[#12305e] text-white flex items-center justify-center group-hover:bg-[#1f6fd1] transition-colors"><Phone size={20} /></span>
              <span className="font-display text-xl font-bold text-slate-900">{CONTACTO.tel}</span>
            </a>
            <a href={`mailto:${CONTACTO.email}`} className="flex items-center gap-4 group" data-testid="contacto-email">
              <span className="h-12 w-12 rounded-2xl bg-[#12305e] text-white flex items-center justify-center group-hover:bg-[#1f6fd1] transition-colors"><Mail size={20} /></span>
              <span className="font-display text-lg font-bold text-slate-900 break-all">{CONTACTO.email}</span>
            </a>
          </div>
          <p className="mt-8 text-sm text-slate-500">Te ayudamos a sacar el fichero de tu programa de gestión.</p>
          <img src="/img/farmanaliza-equipo.jpg?v=2" alt="Equipo de farmacia" className="mt-10 rounded-3xl w-full aspect-[3/2] object-cover hidden lg:block" />
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/60 p-7 sm:p-10 self-start">
          {done ? (
            <div className="py-16 text-center fade-in" data-testid="lead-success">
              <CheckCircle2 size={56} className="mx-auto text-emerald-500" />
              <h3 className="font-display text-2xl font-bold text-slate-900 mt-5">¡Gracias! Hemos recibido tu solicitud</h3>
              <p className="text-slate-600 mt-2">Te contactaremos en menos de 24 horas. Si nos has dejado tu email, ya tienes allí nuestro mensaje de bienvenida.</p>
              <button onClick={() => setDone(false)} className="mt-8 text-sm font-semibold text-[#1f6fd1] hover:underline" data-testid="lead-again">Enviar otra solicitud</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="lead-form">
              <h3 className="font-display text-2xl font-bold text-slate-900">Cuéntanos quién eres</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Nombre y apellidos *"><input required minLength={2} value={f.nombre} onChange={set("nombre")} className={inp} data-testid="lead-nombre" /></Field>
                <Field label="Nombre de la farmacia *"><input required minLength={2} value={f.farmacia} onChange={set("farmacia")} className={inp} data-testid="lead-farmacia" /></Field>
                <Field label="Teléfono"><input type="tel" value={f.telefono} onChange={set("telefono")} className={inp} data-testid="lead-telefono" /></Field>
                <Field label="Email"><input type="email" value={f.email} onChange={set("email")} className={inp} data-testid="lead-email" /></Field>
                <Field label="Población / provincia"><input value={f.poblacion} onChange={set("poblacion")} className={inp} data-testid="lead-poblacion" /></Field>
                <Field label="¿Qué te interesa?">
                  <select value={f.interes} onChange={set("interes")} className={inp} data-testid="lead-interes">
                    <option value="">Elige una opción</option>
                    {INTERESES.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Comentarios"><textarea rows={4} value={f.comentarios} onChange={set("comentarios")} className={inp} data-testid="lead-comentarios" /></Field>
              <input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
              <button type="submit" disabled={busy} className="w-full inline-flex justify-center items-center gap-2 px-6 py-4 rounded-full bg-[#1f6fd1] text-white font-bold hover:bg-[#1658a8] disabled:opacity-60 transition-colors" data-testid="lead-submit">
                {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />} Enviar solicitud
              </button>
              <p className="text-[11px] text-slate-400 leading-relaxed">Responsable: Archiduque Consulting. Usaremos tus datos únicamente para contactarte y darte información sobre Farmanaliza; no los cederemos a terceros. Puedes pedir su supresión escribiendo a {CONTACTO.email}.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#0b1f3f] text-blue-100/70 py-12" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row gap-6 justify-between items-start sm:items-center">
        <div><Logo light /><div className="text-xs mt-1">Gestión de stocks en farmacia · Archiduque Consulting</div></div>
        <div className="text-sm flex flex-wrap gap-5">
          <a href={CONTACTO.telHref} className="hover:text-white">{CONTACTO.tel}</a>
          <a href={`mailto:${CONTACTO.email}`} className="hover:text-white">{CONTACTO.email}</a>
          <a href="/login" className="hover:text-white">Acceder</a>
        </div>
      </div>
    </footer>
  );
}
